import { useState, useRef } from "react";
import { storyblokEditable } from "@storyblok/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";
import { usePathname } from "@lib/i18n/navigation-app-router";
import { useCurrentUser } from "@hooks/use-current-user";
import Button from "@components/core/Button";
import LargeBannerSlide from "@components/core/Storyblok/Home/LargeBannerSlide";

const AUTOPLAY_DELAY = 5000;
const FADE_DURATION = 1000;
const MAX_SLIDES = 4;
const AUTOPLAY_CONFIG = { delay: AUTOPLAY_DELAY, disableOnInteraction: false };
const PAGINATION_CONFIG = { clickable: true };
const NAVIGATION_CONFIG = {
  nextEl: ".large-banner-slider-next",
  prevEl: ".large-banner-slider-prev",
};
const FADE_EFFECT_CONFIG = { crossFade: true };

function LargeBannerSlider({ blok }) {
  const pathname = usePathname();
  const user = useCurrentUser();
  const userGroup = user?.user_group;
  const swiperRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);

  function sendTrackerData(slide, index) {
    const defaultEventData = {
      tracking_name: "homeLargeBannerSlider",
      page: pathname,
      link: slide.link?.url,
      position: index + 1,
      user_group: userGroup,
    };

    ahoy("$click", defaultEventData);
    analytics.track("$click", defaultEventData);
  }

  function toggleAutoplay() {
    const swiper = swiperRef.current;
    if (!swiper) return;

    if (swiper.autoplay.running) {
      swiper.autoplay.stop();
    } else {
      swiper.autoplay.start();
    }
  }

  if (!blok?.slides?.length) return null;

  return (
    <section
      key={blok._uid}
      {...storyblokEditable(blok)}
      className="storyblok-home-container-full-width large-banner-slider-container"
    >
      <Button
        variant="ghost-primary"
        className="large-banner-slider-arrow large-banner-slider-prev"
        leftIconName="chevron-left"
      />
      <Button
        variant="ghost-primary"
        className="large-banner-slider-arrow large-banner-slider-next"
        leftIconName="chevron-right"
      />
      <Button
        variant="ghost-primary"
        size="sm"
        className="large-banner-slider-play-pause"
        leftIconName={isPlaying ? "pause-icon" : "play-icon"}
        onClick={toggleAutoplay}
      />

      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        className="large-banner-slider-swiper"
        loop
        speed={FADE_DURATION}
        autoplay={AUTOPLAY_CONFIG}
        pagination={PAGINATION_CONFIG}
        navigation={NAVIGATION_CONFIG}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onAutoplayStart={() => setIsPlaying(true)}
        onAutoplayStop={() => setIsPlaying(false)}
        effect="fade"
        fadeEffect={FADE_EFFECT_CONFIG}
      >
        {blok.slides.slice(0, MAX_SLIDES).map((slide, index) => (
          <SwiperSlide key={slide._uid}>
            <LargeBannerSlide
              blok={slide}
              isFirst={index === 0}
              onClick={() => sendTrackerData(slide, index)}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}

export default LargeBannerSlider;
